import React, { useEffect } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import { Institutions } from '@acorns/web-bank-linking'
import { ConfirmationModal, ModalActionTypes } from '@acorns/web-components'
import { WithOptimizelyProps } from '@optimizely/react-sdk'
import {
  branch,
  compose,
  defaultProps,
  fromRenderProps,
  renderComponent,
  withHandlers,
  withProps,
  withState,
} from 'recompose'

import { DONT_SEE_YOUR_BANK, MOC_ONBOARDING_FLOW } from 'app-constants'
import CantFindBankSVG from 'assets/icons/institution-icon.svg'
import CenteredSpinner from 'components/CenteredSpinner'
import withHeroText from 'components/HOCs/withHeroText'
import withPartnerProps, { WithPartnerProps } from 'components/HOCs/WithPartnerProps'
import { PlaidLinkProps, withPlaidLinkProps } from 'components/HOCs/WithPlaidLinkProps'
import withRemoteStrings, { RemoteStringsProps } from 'components/HOCs/withRemoteStrings'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import getDefaultEventData from 'components/Trackers/defaults'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { ActionType, FinancialInstitution, PageName } from 'generated/types'
import { getButtonTappedEvent } from 'utils/analytics'
import trackEvent from 'utils/analytics/trackEvent'
import { withApolloErrorHandler } from 'utils/Apollo'
import Environment, { withEnv } from 'utils/environment'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { getVisitedPages } from 'utils/FlowProvider/utils'
import { FunnelContext, FunnelContextProps } from 'utils/FunnelContext'
import { getGeneralError } from 'utils/getGeneralError'
import {
  withSetPrimaryFundingSource,
  withSkipBankLinking,
} from 'utils/mutators/bank-linking/bankLinking'
import { withOptimizely } from 'utils/Optimizely'
import { withPrompt } from 'utils/prompts'

import { processOnPlaidExitError, useFundingSkipLinkText } from './utils'

const DIRECT_BANK_INTEGRATION_FEATURE = 'direct_bank_integration'

type Props = {
  env: Environment
  institutions: FinancialInstitution[]
  loading: boolean
  loadingInstitutions: boolean
  modalIsOpen: boolean
  search: string
  selectedInstitutionId?: string
  header: string
  text: string
  submitting: boolean
}

type HandlerProps = {
  handleInfiniteScroll: () => void
  handleOnChange: () => void
  handleOnSearch: () => void
  handleOnSkip: () => void
  handleSkipConfirm: () => void
  onRequestClose: () => void
  setModalIsOpen: (value: boolean) => void
  setSearch: (value: string) => void
  setSelectedInstitutionId: (id: string) => void
  onInstitutionClick: (institution: { name: string }) => void
  onPlaidLinkExit: () => void
}
type InnerProps = Props &
  HandlerProps &
  WithOptimizelyProps &
  WithPartnerProps &
  RemoteStringsProps &
  WithFlowRouterProps &
  RouteComponentProps &
  PlaidLinkProps &
  FunnelContextProps

const DONT_SEE_BANK = {
  id: DONT_SEE_YOUR_BANK,
  name: 'Don’t see your bank?',
  subText: 'Link with routing & account number',
  logoUrl: CantFindBankSVG,
}
const confirmationModalLabels = {
  confirm: 'Skip',
  cancel: 'Go back',
}

export const enhance = compose<InnerProps, {}>(
  defaultProps({
    loadingInstitutions: true,
  }),
  fromRenderProps<Partial<FunnelContextProps>, {}, Partial<FunnelContextProps>>(
    FunnelContext.Consumer,
    ({ isBackEnabled, hasVisited, isDynamicFunnelEnabled, routeToPage }) => ({
      isBackEnabled,
      hasVisited,
      isDynamicFunnelEnabled,
      routeToPage,
    }),
  ),
  withState('search', 'setSearch', ''),
  withState('selectedInstitutionId', 'setSelectedInstitutionId', null),
  withState('modalIsOpen', 'setModalIsOpen', false),
  withState('submitting', 'setSubmitting', false),
  withEnv,
  withRouter,
  withFlowRouter,
  withPartnerProps,
  withSetPrimaryFundingSource,
  withPrompt,
  withOptimizely,
  withSkipBankLinking,
  withHeroText,
  withRemoteStrings,
  withProps(({ getRemoteString, search, institutions, loading, heroText }) => {
    if (search.length && institutions.length && !loading) {
      institutions.push(DONT_SEE_BANK)
    }

    return {
      header: getRemoteString('institutions_header', heroText.header),
      text: getRemoteString('institutions_text', heroText.text),
    }
  }),
  withApolloErrorHandler('CreatePlaidLinkedAccount', props => ({ graphQLErrors = [] }) => {
    const { title, message, actions } = getGeneralError(graphQLErrors, props, () =>
      window.location.reload(),
    )
    return { actions, message, title }
  }),
  withPlaidLinkProps,
  withHandlers({
    onPlaidLinkExit: ({
      partner,
      promptUser,
      route,
      flow,
      isDynamicFunnelEnabled,
      routeToPage,
      hasVisited,
    }) => error => {
      if (error) {
        processOnPlaidExitError(error)
      }

      const shouldShowSkipButton = isDynamicFunnelEnabled
        ? !hasVisited(PageName.SecondChanceBankLink)
        : !getVisitedPages().includes(FlowRoute.SecondChanceBankLink)

      if (flow !== MOC_ONBOARDING_FLOW && shouldShowSkipButton) {
        promptUser({
          title: 'Go back',
          message:
            'You can go back to choose a different bank, or skip linking. If you skip, you won’t be able to transfer automatic investment like Round-Ups®.',
          actions: [
            {
              type: ModalActionTypes.secondary,
              label: 'Skip',
            },
            {
              type: ModalActionTypes.primary,
              label: 'Go back',
            },
          ],
        }).then(result => {
          if (result.index === 0) {
            trackEvent('PlaidLink:Skip', { location, partner, flow })
            if (isDynamicFunnelEnabled) {
              if (
                hasVisited(PageName.SecondChanceBankLink) ||
                hasVisited(PageName.RecurringInvestment) ||
                hasVisited(PageName.OneTimeInvestment)
              ) {
                routeToPage(PageName.Institutions, ActionType.Continue)
              } else {
                routeToPage(PageName.Institutions, ActionType.Skip)
              }
            } else {
              route({ from: FlowRoute.Institutions, action: 'skip' })
            }
          }
        })
      }
    },
    onInstitutionClick: ({ partner, flow }) => institution => {
      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.INSTITUTIONS.INSTITUTION_CONTAINER),
          ...getDefaultEventData('Button Tapped'),
          cta_title: institution.name,
        },
        location,
        flow,
        partner,
      })
    },
  }),
  withHandlers({
    handleOnSearch: ({ setSearch }) => (searchTerm: string) => {
      setSearch(searchTerm.trim())
    },
    handleOnChange: ({ setSearch }) => (input: string) => {
      setSearch(input.trim())
    },
    handleOnSkip: ({ setModalIsOpen, flow }) => () => {
      setModalIsOpen(true)
      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.INSTITUTIONS.SKIP_LINK_BANK_BUTTON),
          ...getDefaultEventData('Button Tapped'),
          cta_title: flow !== 'spend' ? 'Skip' : null,
        },
      })
    },
    onRequestClose: ({ setModalIsOpen }) => () => {
      setModalIsOpen(false)
      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.INSTITUTIONS.SKIP_LINK_BANK_MODAL_CANCEL),
          ...getDefaultEventData('Button Tapped'),
          cta_title: confirmationModalLabels.cancel,
        },
      })
    },
    handleSkipConfirm: ({
      flow,
      hasVisited,
      isDynamicFunnelEnabled,
      partner,
      route,
      routeToPage,
      trackOptimizely,
    }) => () => {
      trackOptimizely('skip_bank_linking.cta_clicked')
      trackEvent('PlaidLink:Skip', { location, partner, flow })
      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.INSTITUTIONS.SKIP_LINK_BANK_MODAL_CONFIRM),
          ...getDefaultEventData('Button Tapped'),
          cta_title: confirmationModalLabels.confirm,
        },
        flow,
        partner,
      })

      if (isDynamicFunnelEnabled) {
        if (
          hasVisited(PageName.SecondChanceBankLink) ||
          hasVisited(PageName.RecurringInvestment) ||
          hasVisited(PageName.OneTimeInvestment)
        ) {
          routeToPage(PageName.SecondChanceInstitutions, ActionType.Skip)
        } else {
          routeToPage(PageName.Institutions, ActionType.Skip)
        }
      } else {
        route({ from: FlowRoute.Institutions, action: 'skip' })
      }
    },
    handleInfiniteScroll: ({ data: { fetchMore }, search, institutions }) => () => {
      const lastInstitution = institutions[institutions.length - 1].id

      fetchMore({
        variables: { search, pageInput: { first: 50, after: `${lastInstitution}` } },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const hasNextPage = fetchMoreResult.financialInstitutions.pageInfo.hasNextPage

          if (!fetchMoreResult || !hasNextPage) {
            return previousResult
          }

          return {
            financialInstitutions: {
              __typename: previousResult.financialInstitutions.__typename,
              edges: [
                ...previousResult.financialInstitutions.edges,
                ...fetchMoreResult.financialInstitutions.edges,
              ],
              pageInfo: fetchMoreResult.financialInstitutions.pageInfo,
            },
          }
        },
      })
    },
  }),
  branch(({ loading, submitting }) => loading || submitting, renderComponent(CenteredSpinner)),
)

const InstitutionsPage: React.FC<InnerProps> = ({
  flow,
  handleAvailableFundingSource,
  handleLinkAccountError,
  handleOnSkip,
  handleSkipConfirm,
  modalIsOpen,
  route,
  onPlaidLinkEvent,
  onPlaidLinkExit,
  onPlaidLinkSuccess,
  onRequestClose,
  header,
  text,
  env,
  onInstitutionClick,
  optimizely,
  isDynamicFunnelEnabled,
  isBackEnabled,
  routeToPage,
  hasVisited,
}) => {
  const skipLinkText = useFundingSkipLinkText(flow)

  const shouldShowSkipButton = isDynamicFunnelEnabled
    ? !hasVisited(PageName.SecondChanceBankLink)
    : !getVisitedPages().includes(FlowRoute.SecondChanceBankLink)

  const handleDirectBankClick = financialInstitution => {
    isDynamicFunnelEnabled
      ? routeToPage(PageName.Institutions, ActionType.Direct)
      : route({
          from: FlowRoute.Institutions,
          action: 'direct',
          locationState: {
            financialInstitution,
          },
        })
  }

  const handleManualClick = () => {
    trackEvent('Button Tapped', {
      eventData: {
        object_name: 'InstitutionsBankLinkingManualCTA',
        screen: 'InstitutionsBankLinkingManualCTA',
        screen_name: 'InstitutionsBankLinkingManualCTA',
        feature: 'InstitutionsBankLinkingManualCTA',
        event_description: 'Button Tapped',
        object: 'Button',
        action: 'Tapped',
        ...getDefaultEventData('Button Tapped'),
      },
    })
    isDynamicFunnelEnabled
      ? routeToPage(PageName.Institutions, ActionType.Manual)
      : route({ from: FlowRoute.Institutions, action: 'manual' })
  }

  const redirectUrl = `${window.location.origin}/plaid-link-redirect`

  useEffect(() => {
    if (modalIsOpen) {
      trackEvent('Container Viewed', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.INSTITUTIONS.SKIP_LINK_BANK_MODAL),
          ...getDefaultEventData('Container Viewed'),
        },
      })
    }
  }, [modalIsOpen])

  return (
    <>
      <Nav
        back={
          isDynamicFunnelEnabled
            ? !hasVisited(PageName.Institutions)
              ? isBackEnabled(PageName.Institutions)
              : isBackEnabled(PageName.SecondChanceInstitutions)
            : false
        }
        linkText={shouldShowSkipButton ? skipLinkText : undefined}
        onLink={shouldShowSkipButton ? handleOnSkip : undefined}
      />
      <Layout fullWidth>
        <Institutions
          disableRoundUps={flow === 'later' || flow === 'spend'}
          header={header}
          onAvailableFundingSources={handleAvailableFundingSource}
          onDirectBankClick={handleDirectBankClick}
          onEvent={onPlaidLinkEvent}
          onExit={onPlaidLinkExit}
          onInstitutionClick={onInstitutionClick}
          onLinkAccountError={handleLinkAccountError}
          onManualLinkClick={handleManualClick}
          onSuccess={onPlaidLinkSuccess}
          plaidEnv={env.get('plaidEnv')}
          plaidLinkKey={env.get('plaidLinkKey')}
          subheader={text}
          redirectUrl={redirectUrl}
          includeDirectBank={optimizely.isFeatureEnabled(DIRECT_BANK_INTEGRATION_FEATURE) ?? false}
        />
        <ConfirmationModal
          actions={[
            {
              label: confirmationModalLabels.confirm,
              type: ModalActionTypes.secondary,
              handler: handleSkipConfirm,
            },
            {
              label: confirmationModalLabels.cancel,
              type: ModalActionTypes.primary,
              handler: onRequestClose,
            },
          ]}
          onRequestClose={onRequestClose}
          isOpen={modalIsOpen}
          message="In order for you to start saving and investing, Acorns securely connects to your bank to enable Round-Ups® and fund your investments. Your security is our primary concern, so we never store your bank login information."
          title="Are you sure?"
        />
      </Layout>
    </>
  )
}

export { InstitutionsPage as Institutions }
export default enhance(InstitutionsPage)
